import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Swal from "sweetalert2";
import config from "../common/Config";
import "../common/commonStyles.css";
import {
  encryptPayload,
  normalizeEncryptedApiResponse,
} from "../../utils/customerCrypto";
import {
  getCompanySettings,
  resolveCustomerApiEncrypt,
} from "../../utils/companySettingsCache";
import {
  buildSubFieldRows,
  saveLocalSubFieldsMap,
} from "./productFormSubFieldUtils";
import {
  inferPricingModeFromRows,
  pricingModeStorageKey,
  resolvePricingModeSelection,
} from "./productFormFieldDefaults";
import { PRODUCT_TYPE_TO_SPLIT } from "../Master/Products/dynamicFormApi";

const isFieldVisible = (value) => {
  if (value === false || value === 0 || value === "0") return false;
  const text = String(value ?? "").trim().toLowerCase();
  if (text === "false" || text === "no" || text === "off") return false;
  return true;
};

const isFieldRequired = (value) =>
  value === true || value === 1 || value === "1";

const flattenNestedAsRows = (fields = [], sectionTitle = "") => {
  const rows = [];
  (fields || []).forEach((field) => {
    if (!field?.name) return;
    rows.push({
      name: field.name,
      label: field.label || field.name,
      section: sectionTitle,
      visible: isFieldVisible(field.visible),
      required: isFieldRequired(field.required),
      subFields: [],
    });
    if (Array.isArray(field.fields) && field.fields.length) {
      rows.push(...flattenNestedAsRows(field.fields, field.label || sectionTitle));
    }
  });
  return rows;
};

const mapTopLevelRows = (fields = [], sectionTitle = "", enableSubFields = false) =>
  (fields || [])
    .filter((field) => field?.name)
    .map((field) => ({
      name: field.name,
      label: field.label || field.name,
      section: sectionTitle,
      visible: isFieldVisible(field.visible),
      required: isFieldRequired(field.required),
      subFields: enableSubFields
        ? buildSubFieldRows(field.name, field.fields || field.sub_fields || [])
        : [],
    }));

const collectFieldRows = (form, enableSubFields = false) => {
  if (!form) return [];
  const seen = new Set();
  const rows = [];

  const pushUnique = (list) => {
    list.forEach((row) => {
      if (seen.has(row.name)) return;
      seen.add(row.name);
      rows.push(row);
    });
  };

  if (Array.isArray(form.sections) && form.sections.length) {
    form.sections.forEach((section) => {
      if (enableSubFields) {
        pushUnique(
          mapTopLevelRows(section.fields || [], section.title || "", true),
        );
      } else {
        pushUnique(flattenNestedAsRows(section.fields || [], section.title || ""));
      }
    });
  }

  if (Array.isArray(form.fields) && form.fields.length) {
    if (enableSubFields) {
      pushUnique(mapTopLevelRows(form.fields, form.title || "", true));
    } else {
      pushUnique(flattenNestedAsRows(form.fields, form.title || ""));
    }
  }

  return rows;
};

const EMPTY_DEFAULT_FIELDS = [];

const mergeWithDefaultRows = (apiRows = [], defaultRows = [], resolveName) => {
  if (!defaultRows.length) return apiRows;
  if (!apiRows.length) return defaultRows;

  const defaultByName = new Map();
  defaultRows.forEach((row) => {
    const key = resolveName ? resolveName(row.name) : row.name;
    defaultByName.set(key, { ...row, name: key });
  });

  const used = new Set();
  const merged = apiRows.map((apiRow) => {
    const key = resolveName ? resolveName(apiRow.name) : apiRow.name;
    used.add(key);
    const defaultRow = defaultByName.get(key);
    const nestedFromApi = (apiRow.subFields || []).map((sub) => ({
      name: sub.name,
      label: sub.label,
      visible: sub.visible,
    }));
    const subFields = buildSubFieldRows(
      defaultRow?.name || apiRow.name,
      nestedFromApi,
    );
    return {
      ...(defaultRow || {}),
      ...apiRow,
      name: key,
      sourceNames: [apiRow.name].filter(Boolean),
      label: apiRow.label || defaultRow?.label || key,
      visible: isFieldVisible(apiRow.visible),
      required: isFieldRequired(apiRow.required),
      subFields: subFields.length
        ? subFields
        : apiRow.subFields?.length
          ? apiRow.subFields
          : defaultRow?.subFields || [],
    };
  });

  defaultRows.forEach((defaultRow) => {
    const key = resolveName ? resolveName(defaultRow.name) : defaultRow.name;
    if (used.has(key)) return;
    merged.push({
      ...defaultRow,
      name: key,
      visible: false,
      required: false,
      subFields: buildSubFieldRows(defaultRow.name, []).length
        ? buildSubFieldRows(defaultRow.name, [])
        : defaultRow.subFields || [],
    });
  });

  const mergedByName = new Map();
  merged.forEach((row) => {
    if (!row?.name) return;
    const existing = mergedByName.get(row.name);
    if (!existing) {
      mergedByName.set(row.name, row);
      return;
    }
    const hidden = existing.visible === false || row.visible === false;
    const sourceNames = [
      ...new Set([...(existing.sourceNames || []), ...(row.sourceNames || [])]),
    ];
    mergedByName.set(row.name, {
      ...existing,
      ...row,
      name: row.name,
      sourceNames,
      visible: hidden ? false : row.visible,
      required: hidden ? false : row.required,
      label: row.label || existing.label,
    });
  });

  const ordered = [];
  defaultRows.forEach((defaultRow) => {
    const key = resolveName ? resolveName(defaultRow.name) : defaultRow.name;
    if (!key || !mergedByName.has(key)) return;
    ordered.push(mergedByName.get(key));
    mergedByName.delete(key);
  });
  mergedByName.forEach((row) => ordered.push(row));

  return ordered;
};

export default function FormFieldVisibilitySettings({
  title,
  formKey,
  module,
  fetchForm,
  saveEndpoint = "/get_dynamic_form",
  enableSubFields = false,
  defaultFields = EMPTY_DEFAULT_FIELDS,
  resolveFieldName,
  normalizeRows,
  lockedFieldNames = EMPTY_DEFAULT_FIELDS,
  highlightFieldNames = EMPTY_DEFAULT_FIELDS,
  fieldParts = EMPTY_DEFAULT_FIELDS,
  splitColumns = 1,
  modeFilter = null,
  linkedFieldGroups = EMPTY_DEFAULT_FIELDS,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [modeFilterValue, setModeFilterValue] = useState(
    () => String(modeFilter?.defaultValue || ""),
  );

  const lockedFieldSet = useMemo(() => {
    const set = new Set();
    (lockedFieldNames || []).forEach((name) => {
      const key = resolveFieldName ? resolveFieldName(name) : name;
      if (key) set.add(key);
      if (name) set.add(name);
    });
    return set;
  }, [lockedFieldNames, resolveFieldName]);

  const highlightFieldSet = useMemo(() => {
    const set = new Set();
    (highlightFieldNames || []).forEach((name) => {
      const key = resolveFieldName ? resolveFieldName(name) : name;
      if (key) set.add(key);
      if (name) set.add(name);
    });
    return set;
  }, [highlightFieldNames, resolveFieldName]);

  const isFieldLocked = (name = "") => lockedFieldSet.has(name);
  const isFieldHighlighted = (name = "") =>
    highlightFieldSet.has(name) || lockedFieldSet.has(name);

  const modeFilterMeta = useMemo(() => {
    if (!modeFilter?.afterField || !Array.isArray(modeFilter?.options)) {
      return null;
    }
    const resolveName = (name) =>
      resolveFieldName ? resolveFieldName(name) : name;
    const afterField = resolveName(modeFilter.afterField);
    const options = modeFilter.options.map((option) => {
      const defaultFieldStates = {};
      Object.entries(option.defaultFieldStates || {}).forEach(([name, state]) => {
        const key = resolveName(name);
        if (!key) return;
        defaultFieldStates[key] = {
          visible: state?.visible !== false,
          required: state?.required === true,
        };
      });
      return {
        value: String(option.value || ""),
        label: option.label || option.value,
        fieldNames: new Set(
          (option.fieldNames || []).map((name) => resolveName(name)).filter(Boolean),
        ),
        defaultFieldStates,
      };
    });
    const controlledFields = new Set();
    options.forEach((option) => {
      option.fieldNames.forEach((name) => controlledFields.add(name));
    });
    return { afterField, options, controlledFields };
  }, [modeFilter, resolveFieldName]);

  const activeModeFieldNames = useMemo(() => {
    if (!modeFilterMeta) return null;
    if (!modeFilterValue) return new Set();
    const active = modeFilterMeta.options.find(
      (option) => option.value === modeFilterValue,
    );
    return active?.fieldNames || new Set();
  }, [modeFilterMeta, modeFilterValue]);

  const applyModeToRows = useCallback((sourceRows, modeValue, options = {}) => {
    if (!modeFilterMeta || !modeValue || !Array.isArray(sourceRows)) {
      return sourceRows;
    }
    const preserveVisibility = options.preserveVisibility === true;
    const active = modeFilterMeta.options.find(
      (option) => option.value === modeValue,
    );
    if (!active) return sourceRows;
    const defaults = active.defaultFieldStates || {};
    const activeFields = active.fieldNames || new Set();
    return sourceRows.map((row) => {
      const disableRow = (rowItem) => ({
        ...rowItem,
        visible: false,
        required: false,
        subFields: (rowItem.subFields || []).map((sub) => ({
          ...sub,
          visible: false,
        })),
      });
      const enableRow = (rowItem, required = rowItem.required) => ({
        ...rowItem,
        visible: true,
        required: !!required,
        subFields: (rowItem.subFields || []).map((sub) => ({
          ...sub,
          visible: true,
        })),
      });
      const keepApiRow = (rowItem, required = rowItem.required) => ({
        ...rowItem,
        required: !!required,
        subFields: (rowItem.subFields || []).map((sub) => ({
          ...sub,
          visible: rowItem.visible ? !!sub.visible : false,
        })),
      });

      if (defaults[row.name]) {
        const state = defaults[row.name];
        if (!state.visible) return disableRow(row);
        if (preserveVisibility) {
          return keepApiRow(row, row.required);
        }
        return enableRow(row, state.required);
      }

      if (!modeFilterMeta.controlledFields.has(row.name)) {
        return row;
      }

      if (!activeFields.has(row.name)) {
        return disableRow(row);
      }

      if (preserveVisibility) {
        return keepApiRow(row, row.required);
      }

      return enableRow(row, row.required);
    });
  }, [modeFilterMeta]);

  const applyModeDefaultFieldStates = (modeValue) => {
    if (!modeFilterMeta || !modeValue) return;
    setRows((prev) => applyModeToRows(prev, modeValue));
  };

  const handleModeFilterChange = (value) => {
    setModeFilterValue(value);
    applyModeDefaultFieldStates(value);
  };

  const resolveLoadedPricingMode = (form, sourceRows) => {
    const options = modeFilterMeta?.options || modeFilter?.options || [];
    const fromApi =
      resolvePricingModeSelection(form?.pricing_type, options) ||
      resolvePricingModeSelection(form?.product_type, options) ||
      resolvePricingModeSelection(form?.split, options);
    let fromLocal = "";
    try {
      fromLocal = resolvePricingModeSelection(
        localStorage.getItem(pricingModeStorageKey(formKey)),
        options,
      );
    } catch (err) {
      fromLocal = "";
    }
    const inferred = inferPricingModeFromRows(sourceRows, options);
    return (
      fromApi ||
      fromLocal ||
      inferred ||
      String(modeFilter?.defaultValue || "")
    );
  };

  const rowParts = useMemo(() => {
    if (fieldParts?.length) {
      const byName = new Map(rows.map((row) => [row.name, row]));
      const used = new Set();
      const parts = fieldParts.map((part) => {
        const partRows = [];
        (part.names || []).forEach((name) => {
          const key = resolveFieldName ? resolveFieldName(name) : name;
          const row = byName.get(key) || byName.get(name);
          if (!row || used.has(row.name)) return;
          partRows.push(row);
          used.add(row.name);
        });
        return { title: part.title || "", rows: partRows };
      });
      const remaining = rows.filter((row) => !used.has(row.name));
      if (remaining.length) {
        if (parts.length) {
          parts[parts.length - 1].rows = [
            ...parts[parts.length - 1].rows,
            ...remaining,
          ];
        } else {
          parts.push({ title: "", rows: remaining });
        }
      }
      return parts.filter((part) => part.rows.length);
    }

    const visibleRows =
      modeFilterMeta && activeModeFieldNames
        ? rows.filter((row) => {
            if (!modeFilterMeta.controlledFields.has(row.name)) return true;
            return activeModeFieldNames.has(row.name);
          })
        : rows;

    const columns = Math.max(1, Number(splitColumns) || 1);
    if (columns <= 1 || !visibleRows.length) {
      return [{ title: "", rows: visibleRows }];
    }
    const size = Math.ceil(visibleRows.length / columns);
    const parts = [];
    for (let i = 0; i < columns; i += 1) {
      const chunk = visibleRows.slice(i * size, (i + 1) * size);
      if (chunk.length) parts.push({ title: "", rows: chunk });
    }
    return parts;
  }, [
    activeModeFieldNames,
    fieldParts,
    modeFilterMeta,
    resolveFieldName,
    rows,
    splitColumns,
  ]);

  const loadFields = useCallback(async () => {
    setLoading(true);
    try {
      let apiRows = [];
      let formData = null;
      try {
        formData = await fetchForm({ formKey });
        apiRows = collectFieldRows(formData, enableSubFields);
      } catch {
        apiRows = [];
      }
      let merged = mergeWithDefaultRows(apiRows, defaultFields, resolveFieldName);
      if (typeof normalizeRows === "function") {
        merged = normalizeRows(merged.length ? merged : apiRows);
      }
      const nextRows = merged.length ? merged : apiRows;
      const loadedMode = resolveLoadedPricingMode(formData, nextRows);
      setModeFilterValue(loadedMode);
      setRows(
        loadedMode
          ? applyModeToRows(nextRows, loadedMode, { preserveVisibility: true })
          : nextRows,
      );
      if (!merged.length && !apiRows.length) {
        Swal.fire("Error", "Failed to load form fields", "error");
      }
    } catch (error) {
      const fallback = typeof normalizeRows === "function"
        ? normalizeRows(defaultFields)
        : defaultFields;
      const fallbackRows = fallback.length ? fallback : [];
      const loadedMode = resolveLoadedPricingMode(null, fallbackRows);
      setModeFilterValue(loadedMode);
      setRows(
        loadedMode
          ? applyModeToRows(fallbackRows, loadedMode, { preserveVisibility: true })
          : fallbackRows,
      );
      if (!defaultFields.length) {
        Swal.fire(
          "Error",
          error?.message || "Failed to load form fields",
          "error",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [
    applyModeToRows,
    defaultFields,
    enableSubFields,
    fetchForm,
    formKey,
    modeFilter,
    modeFilterMeta,
    normalizeRows,
    resolveFieldName,
  ]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const handleVisibleChange = (name, checked) => {
    if (isFieldLocked(name)) return;
    const group = (linkedFieldGroups || []).find((item) => item.parent === name);
    const childSet = new Set(group?.children || []);
    setRows((prev) =>
      prev.map((row) => {
        const isParent = row.name === name;
        const isChild = childSet.has(row.name);
        if (!isParent && !isChild) return row;
        if (!checked) {
          return {
            ...row,
            visible: false,
            required: false,
            subFields: (row.subFields || []).map((sub) => ({
              ...sub,
              visible: false,
            })),
          };
        }
        if (isParent) return { ...row, visible: true };
        return row;
      }),
    );
  };

  const handleMandatoryChange = (name, value) => {
    if (isFieldLocked(name)) return;
    setRows((prev) =>
      prev.map((row) => {
        if (row.name !== name) return row;
        const required = value === "yes";
        return {
          ...row,
          required,
          visible: required ? true : row.visible,
        };
      }),
    );
  };

  const handleSubFieldChange = (fieldName, subName, checked) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.name !== fieldName) return row;
        const subFields = (row.subFields || []).map((sub) =>
          sub.name === subName ? { ...sub, visible: checked } : sub,
        );
        const anyVisible = subFields.some((sub) => sub.visible);
        return {
          ...row,
          subFields,
          visible: checked || row.visible || anyVisible,
        };
      }),
    );
  };

  const activeModeOption = useMemo(() => {
    if (!modeFilterMeta || !modeFilterValue) return null;
    return (
      modeFilterMeta.options.find(
        (option) => option.value === modeFilterValue,
      ) || null
    );
  }, [modeFilterMeta, modeFilterValue]);

  const payloadFields = useMemo(() => {
    const activeFields = activeModeOption?.fieldNames || null;
    const defaults = activeModeOption?.defaultFieldStates || {};
    return rows.flatMap((row) => {
      const hiddenByMode =
        defaults[row.name]?.visible === false ||
        !!(
          modeFilterMeta &&
          activeFields &&
          modeFilterMeta.controlledFields.has(row.name) &&
          !activeFields.has(row.name)
        );
      const field = {
        name: row.name,
        visible: hiddenByMode ? 0 : row.visible ? 1 : 0,
        required: hiddenByMode ? 0 : row.required ? 1 : 0,
      };
      if (enableSubFields && (row.subFields || []).length) {
        field.sub_fields = row.subFields.map((sub) => ({
          name: sub.name,
          visible: hiddenByMode || !row.visible ? 0 : sub.visible ? 1 : 0,
        }));
      }
      const names =
        row.name === "suppliers"
          ? [
              ...new Set([
                "suppliers",
                "supplier",
                "select_supplier",
                "select_suppliers",
                "selected_suppliers",
                "selected_supplier",
                "supplier_id",
                ...(row.sourceNames || []),
              ]),
            ]
          : [row.name];
      return names.map((name) => ({ ...field, name }));
    });
  }, [activeModeOption, enableSubFields, modeFilterMeta, rows]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("accessToken");
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const pricingTypeValue =
        activeModeOption?.value ||
        modeFilterValue ||
        String(modeFilter?.defaultValue || "");
      const pricingTypeLabel =
        activeModeOption?.label ||
        modeFilterMeta?.options.find((option) => option.value === pricingTypeValue)
          ?.label ||
        "";
      const pricingTypeKey = modeFilter?.payloadKey || "pricing_type";
      const splitValue = PRODUCT_TYPE_TO_SPLIT[pricingTypeValue] ?? "";
      const requestData = {
        form: formKey,
        form_key: formKey,
        form_name: formKey,
        module,
        type: formKey,
        fields: JSON.stringify(payloadFields),
      };
      const encryptBody = {
        form: formKey,
        form_key: formKey,
        form_name: formKey,
        module,
        type: formKey,
        fields: payloadFields,
      };
      if (pricingTypeValue) {
        requestData[pricingTypeKey] = pricingTypeValue;
        requestData.pricing_type = pricingTypeValue;
        requestData.pricing_type_label = pricingTypeLabel;
        requestData.product_type = pricingTypeValue;
        if (splitValue !== "") requestData.split = splitValue;
        encryptBody[pricingTypeKey] = pricingTypeValue;
        encryptBody.pricing_type = pricingTypeValue;
        encryptBody.pricing_type_label = pricingTypeLabel;
        encryptBody.product_type = pricingTypeValue;
        if (splitValue !== "") encryptBody.split = splitValue;
        try {
          localStorage.setItem(pricingModeStorageKey(formKey), pricingTypeValue);
        } catch (err) {
        }
      }

      const formData = new FormData();
      Object.entries(requestData).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        formData.append(key, String(value));
      });
      if (useEncrypt) {
        const encryptedPayload = encryptPayload(encryptBody);
        if (encryptedPayload) {
          formData.append("encrypted_payload", encryptedPayload);
        }
      }

      const response = await fetch(`${config.API_URL}${saveEndpoint}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const json = await response.json().catch(() => ({}));
      const resolved = normalizeEncryptedApiResponse(json, useEncrypt);

      if (!response.ok && resolved?.success !== true) {
        throw new Error(resolved?.message || "Failed to save field settings");
      }

      if (enableSubFields) {
        const localMap = {};
        rows.forEach((row) => {
          if (!(row.subFields || []).length) return;
          localMap[row.name] = {};
          row.subFields.forEach((sub) => {
            localMap[row.name][sub.name] = !!sub.visible;
          });
        });
        saveLocalSubFieldsMap(localMap);
      }

      Swal.fire(
        "Success",
        resolved?.message || "Field settings updated successfully",
        "success",
      );
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to save field settings",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="page-container">
      <Paper className="paper-style">
        <Box sx={{ px: 2, py: 1.2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography className="page-title">{title}</Typography>
            <Button
              variant="contained"
              className="button-variant-contained"
              onClick={handleSave}
              disabled={loading || saving || !rows.length}
            >
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ ml: 2, color: "#637381" }}>
                Loading fields...
              </Typography>
            </Box>
          ) : !rows.length ? (
            <Typography sx={{ color: "#637381", py: 3 }}>
              No form fields available.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: rowParts.length > 1 ? "1fr 1fr" : "1fr",
                },
                gap: 2,
                alignItems: "start",
              }}
            >
              {rowParts.map((part, partIndex) => (
                <Box key={part.title || `fields-${partIndex}`}>
                  {part.title ? (
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 600,
                        mb: 1,
                        color: "#212B36",
                      }}
                    >
                      {part.title}
                    </Typography>
                  ) : null}
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ border: "1px solid rgba(145, 158, 171, 0.16)" }}
                  >
                    <Table size="small" className="table-cell-border">
                      <TableHead className="table-cell-header">
                        <TableRow>
                          <TableCell
                            sx={{ width: enableSubFields ? "24%" : "45%" }}
                          >
                            Field Name
                          </TableCell>
                          {enableSubFields ? (
                            <TableCell sx={{ width: "36%" }}>Sub Fields</TableCell>
                          ) : null}
                          <TableCell
                            align="center"
                            sx={{ width: enableSubFields ? "18%" : "25%" }}
                          >
                            Show / Hide
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ width: enableSubFields ? "22%" : "30%" }}
                          >
                            Mandatory
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {part.rows.map((row) => {
                          const locked = isFieldLocked(row.name);
                          const highlighted = isFieldHighlighted(row.name);
                          const showModeRadios =
                            modeFilterMeta &&
                            row.name === modeFilterMeta.afterField;
                          return (
                            <React.Fragment key={row.name}>
                              <TableRow hover>
                                <TableCell>
                                  <Typography
                                    className={highlighted ? undefined : "commonColor"}
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 500,
                                      color: highlighted ? "#FF0000" : undefined,
                                    }}
                                  >
                                    {row.label}
                                  </Typography>
                                </TableCell>
                                {enableSubFields ? (
                                  <TableCell>
                                    {(row.subFields || []).length ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: 0.5,
                                          maxWidth: 520,
                                        }}
                                      >
                                        {row.subFields.map((sub) => (
                                          <FormControlLabel
                                            key={`${row.name}-${sub.name}`}
                                            control={
                                              <Checkbox
                                                checked={!!sub.visible}
                                                disabled={!row.visible}
                                                onChange={(e) =>
                                                  handleSubFieldChange(
                                                    row.name,
                                                    sub.name,
                                                    e.target.checked,
                                                  )
                                                }
                                                size="small"
                                                sx={{
                                                  color: "#212B36",
                                                  "&.Mui-checked": {
                                                    color: "#F8991D",
                                                  },
                                                }}
                                              />
                                            }
                                            label={
                                              <Typography
                                                sx={{ fontSize: "12px" }}
                                              >
                                                {sub.label}
                                              </Typography>
                                            }
                                            sx={{ m: 0, mr: 1 }}
                                          />
                                        ))}
                                      </Box>
                                    ) : (
                                      <Typography
                                        sx={{ fontSize: "12px", color: "#919EAB" }}
                                      >
                                        —
                                      </Typography>
                                    )}
                                  </TableCell>
                                ) : null}
                                <TableCell align="center">
                                  <Checkbox
                                    checked={row.visible}
                                    disabled={locked}
                                    onChange={(e) =>
                                      handleVisibleChange(
                                        row.name,
                                        e.target.checked,
                                      )
                                    }
                                    size="small"
                                    sx={{
                                      color: "#212B36",
                                      "&.Mui-checked": { color: "#F8991D" },
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <RadioGroup
                                    row
                                    value={row.required ? "yes" : "no"}
                                    onChange={(e) =>
                                      handleMandatoryChange(
                                        row.name,
                                        e.target.value,
                                      )
                                    }
                                    sx={{ justifyContent: "center" }}
                                  >
                                    <FormControlLabel
                                      value="yes"
                                      control={
                                        <Radio
                                          size="small"
                                          disabled={locked || !row.visible}
                                          sx={{
                                            color: "#212B36",
                                            "&.Mui-checked": {
                                              color: "#F8991D",
                                            },
                                          }}
                                        />
                                      }
                                      label={
                                        <Typography sx={{ fontSize: "13px" }}>
                                          Yes
                                        </Typography>
                                      }
                                      sx={{ m: 0, mr: 1.5 }}
                                    />
                                    <FormControlLabel
                                      value="no"
                                      control={
                                        <Radio
                                          size="small"
                                          disabled={locked || !row.visible}
                                          sx={{
                                            color: "#212B36",
                                            "&.Mui-checked": {
                                              color: "#F8991D",
                                            },
                                          }}
                                        />
                                      }
                                      label={
                                        <Typography sx={{ fontSize: "13px" }}>
                                          No
                                        </Typography>
                                      }
                                      sx={{ m: 0 }}
                                    />
                                  </RadioGroup>
                                </TableCell>
                              </TableRow>
                              {showModeRadios ? (
                                <TableRow>
                                  <TableCell
                                    colSpan={enableSubFields ? 4 : 3}
                                    sx={{ py: 1.5, backgroundColor: "#F9FAFB" }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "14px",
                                          fontWeight: 600,
                                          color: "#212B36",
                                          mr: 1,
                                        }}
                                      >
                                        Pricing Type
                                      </Typography>
                                      <RadioGroup
                                        row
                                        value={modeFilterValue}
                                        onChange={(e) =>
                                          handleModeFilterChange(e.target.value)
                                        }
                                      >
                                        {modeFilterMeta.options.map((option) => (
                                          <FormControlLabel
                                            key={option.value}
                                            value={option.value}
                                            control={
                                              <Radio
                                                size="small"
                                                sx={{
                                                  color: "#212B36",
                                                  "&.Mui-checked": {
                                                    color: "#F8991D",
                                                  },
                                                }}
                                              />
                                            }
                                            label={
                                              <Typography
                                                sx={{ fontSize: "13px" }}
                                              >
                                                {option.label}
                                              </Typography>
                                            }
                                            sx={{ m: 0, mr: 2 }}
                                          />
                                        ))}
                                      </RadioGroup>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ) : null}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
